import { DateTime } from 'luxon';
import type { LatestSurveyResponse } from '../../shared/surveys';
import { MedEvent } from '../../components/Shared/ImportMedicationRollStepFormTypes';
import {
  LoadScheduleMatchItem,
  MedEventImages,
  MedEventType,
} from '../../generated';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { IssueSeverity } from '../../shared/constants';

export enum HubOwnerStoreActionTypes {
  CREATE_MEDICATION_ROLL_SUCCESS = 'CREATE_MEDICATION_ROLL_SUCCESS',
  GET_MEDICATION_SCHEDULE_TEMPLATE = 'GET_MEDICATION_SCHEDULE_TEMPLATE',
  RESET_IMPORT_MED_ROLL_FORM = 'RESET_IMPORT_MED_ROLL_FORM',
  GET_MEDICATION_SCHEDULE = 'GET_MEDICATION_SCHEDULE',
  GET_MEDICATION_EVENT_HISTORY = 'GET_MEDICATION_EVENT_HISTORY',
  REQUEST_MEDICATION_EVENT_IMAGES = 'REQUEST_MEDICATION_EVENT_IMAGES',
  GET_MEDICATION_EVENT_IMAGES = 'GET_MEDICATION_EVENT_IMAGES',
  SET_LOADING = 'SET_LOADING',
  UNSET_LOADING = 'UNSET_LOADING',
  FETCH_HUB_OWNERS_SUCCESS = 'FETCH_HUB_OWNERS_SUCCESS',
  ADD_HUB_OWNER_SUCCESS = 'ADD_HUB_OWNER_SUCCESS',
  RESET_ADD_HUB_OWNER_SUCCESS = 'RESET_ADD_HUB_OWNER_SUCCESS',
  EDIT_OWNER_SUCCESS = 'EDIT_OWNER_SUCCESS',
  SET_ACTION_STATUS = 'SET_ACTION_STATUS',
  CLEAR_ACTION_STATUS = 'CLEAR_ACTION_STATUS',
  SET_ADDING_HUB_OWNER = 'SET_ADDING_HUB_OWNER',
  FETCH_OWNER_CONTACTS_SUCCESS = 'FETCH_OWNER_CONTACTS_SUCCESS',
  FETCH_MEDICATION_LIST_SUCCESS = 'FETCH_MEDICATION_LIST_SUCCESS',
  FETCH_OWNER_OVERVIEW_SUCCESS = 'FETCH_OWNER_OVERVIEW_SUCCESS',
  SET_OWNER_OVERVIEW_LOADING = 'SET_OWNER_OVERVIEW_LOADING',
  SET_CARETEAM_ROLE_LOADING = 'SET_CARETEAM_ROLE_LOADING',
  SET_DELETE_CONTACT_LOADING = 'SET_DELETE_CONTACT_LOADING',
  SET_OWNER_CONTACTS_LOADING = 'SET_OWNER_CONTACTS_LOADING',
  REQUEST_OWNER_SURVEY_INSIGHTS = 'REQUEST_OWNER_SURVEY_INSIGHTS',
  FETCH_OWNER_SURVEY_INSIGHTS_SUCCESS = 'FETCH_OWNER_SURVEY_INSIGHTS_SUCCESS',
  FETCH_OWNER_SURVEY_INSIGHTS_FAILURE = 'FETCH_OWNER_SURVEY_INSIGHTS_FAILURE',
}

export enum HubApiActions {
  ADD_HUB_OWNER = 'ADD_HUB_OWNER',
  UPDATE_CARETEAM_ROLE = 'UPDATE_CARETEAM_ROLE',
  DELETE_OWNER_CONTACT = 'DELETE_OWNER_CONTACT',
  CREATE_CARETEAM_INVITE = 'CREATE_CARETEAM_INVITE',
  DELETE_CARETEAM_INVITE = 'DELETE_CARETEAM_INVITE',
  RESEND_CARETEAM_INVITE = 'RESEND_CARETEAM_INVITE',
}

export type CreateMedicationRollSuccessAction = {
  type: HubOwnerStoreActionTypes.CREATE_MEDICATION_ROLL_SUCCESS;
};

export type GetMedicationScheduleTemplateAction = {
  type: HubOwnerStoreActionTypes.GET_MEDICATION_SCHEDULE_TEMPLATE;
  payload: { medScheduleTemplate: CreateMedRollTemplate };
};

export type GetMedicationScheduleAction = {
  type: HubOwnerStoreActionTypes.GET_MEDICATION_SCHEDULE;
  payload: {
    medSchedule: LoadScheduleMatchItem[];
    schedule_local_timezone?: string;
    med_window_time_sec?: number;
  };
};

export type GetMedicationEventHistoryAction = {
  type: HubOwnerStoreActionTypes.GET_MEDICATION_EVENT_HISTORY;
  payload: {
    ownerId: number;
    medEventId: string;
    medEventHistory: MedEventHistory;
  };
};

export type RequestMedicationEventImagesAction = {
  type: HubOwnerStoreActionTypes.REQUEST_MEDICATION_EVENT_IMAGES;
  payload: { requestId: number };
};

export type GetMedicationEventImagesAction = {
  type: HubOwnerStoreActionTypes.GET_MEDICATION_EVENT_IMAGES;
  payload: {
    ownerId: number;
    medEventId: string;
    medEventImages: MedEventImages;
    /** Matches the REQUEST_MEDICATION_EVENT_IMAGES that started this fetch */
    requestId: number;
  };
};

export type ResetImportMedRollFormAction = {
  type: HubOwnerStoreActionTypes.RESET_IMPORT_MED_ROLL_FORM;
};

export type HubOwnerStore = {
  medScheduleTemplate?: CreateMedRollTemplate;
  importMedicationRollSuccess: boolean;
  medSchedule?: LoadScheduleMatchItem[];
  schedule_local_timezone?: string;
  med_window_time_sec?: number;
  medEventHistory?: MedEventHistoryState;
  /** Sequence number of the latest images fetch; older responses are dropped */
  medEventImagesRequestId?: number;
  medEventImages?: MedEventImagesState;
  hubOwners?: HubOwner[];
  addHubOwnerSuccess: boolean;
  isLoading: boolean;
  actionStatus: HubActionStatus;
  isAddingHubOwner: boolean;
  ownerContacts?: OwnerContact[];
  medicationList?: MedicationList;
  medicationStats?: MedicationStats | null;
  medicationStatsOwnerId?: number | null;
  ownerOverview: OwnerOverview | null;
  ownerOverviewLoading: boolean;
  /** Patient Insights tab: GET /owners/:id/survey_insights for one window */
  ownerSurveyInsights: OwnerSurveyInsightsState | null;
  ownerSurveyInsightsLoading: boolean;
  ownerSurveyInsightsError: string | null;
  /** Sequence number of the latest insights fetch; older responses are dropped */
  ownerSurveyInsightsRequestId: number;
  careteamRoleLoading: boolean;
  deleteContactLoading: boolean;
  ownerContactsLoading: boolean;
};

export type MedicationListItem = {
  national_drug_code: string;
  name: string;
  roll_packaged: boolean;
  is_prn: boolean;
  drug_usage_status_id?: number | null;
  time_tags: string[];
  status?: string | null;
  medication_type?: string;
  prescribed_time?: string;
};

export type MedDetail = {
  prescribed_datetime: string;
  medication_dose_status_id: number;
  status: string;
  dose_count: number;
  event_type: string;
};

export type OwnerOverview = {
  ownerId: number; // Keeping ownerId for catching
  // Label of the latest hub-experience (CSAT) response; null when none.
  last_survey_response: string | null;
  // Latest daily wellness check (DWS) score 1-5; null when none. Absent on older backends.
  last_wellness_score?: number | null;
  // Latest response per survey type (OI-1769). Absent on older backends.
  survey_responses?: LatestSurveyResponse[];
  adherence_count: {
    adherent: number;
    'non-adherent': number;
    unknown: number;
    total: number;
  };
  active_issue: {
    high: number;
    medium: number;
    low: number;
  };
  med_details: MedDetail[];
};

export type MedicationList = MedicationListItem[];

export type MedicationStat = {
  count?: number;
  datetime?: string | null;
  description?: string;
};

export type MedicationStats = {
  total_prescribed_meds: MedicationStat;
  pouched_meds: MedicationStat;
  non_pouched_meds: MedicationStat;
  med_events_remaining: MedicationStat;
  days_remaining: MedicationStat;
  med_roll_end_date: MedicationStat;
};

export interface UpdateCareteamRoleReq {
  new_careteam_role_id: number;
}

export type FetchMedicationListSuccessAction = {
  type: HubOwnerStoreActionTypes.FETCH_MEDICATION_LIST_SUCCESS;
  payload: {
    ownerId: number;
    medications: MedicationList;
    stats: MedicationStats | null;
  };
};
export type CreateMedRollTemplate = {
  medEvents: MedEvent[];
  date?: DateTime;
  batchId?: Number;
};

export type SetLoadingAction = {
  type: HubOwnerStoreActionTypes.SET_LOADING;
  payload: boolean;
};

export type UnsetLoadingAction = {
  type: HubOwnerStoreActionTypes.UNSET_LOADING;
};

export type FetchHubOwnersSuccessAction = {
  type: HubOwnerStoreActionTypes.FETCH_HUB_OWNERS_SUCCESS;
  payload: HubOwner[];
};

export type AddHubOwnerSuccessAction = {
  type: HubOwnerStoreActionTypes.ADD_HUB_OWNER_SUCCESS;
  payload: { message: string };
};

export type ResetAddHubOwnerSuccessAction = {
  type: HubOwnerStoreActionTypes.RESET_ADD_HUB_OWNER_SUCCESS;
};

export type EditOwnerSuccessAction = {
  type: HubOwnerStoreActionTypes.EDIT_OWNER_SUCCESS;
  payload: { ownerId: number; requestBody: EditOwnerReqParams };
};

export type SetActionStatusAction = {
  type: HubOwnerStoreActionTypes.SET_ACTION_STATUS;
  payload: HubActionStatus;
};

export type ClearActionStatusAction = {
  type: HubOwnerStoreActionTypes.CLEAR_ACTION_STATUS;
};

export type SetAddingHubOwnerAction = {
  type: HubOwnerStoreActionTypes.SET_ADDING_HUB_OWNER;
  payload: boolean;
};

export interface FetchOwnerContactsSuccessAction {
  type: HubOwnerStoreActionTypes.FETCH_OWNER_CONTACTS_SUCCESS;
  payload: OwnerContact[];
}

export type FetchOwnerOverviewSuccessAction = {
  type: HubOwnerStoreActionTypes.FETCH_OWNER_OVERVIEW_SUCCESS;
  payload: OwnerOverview;
};

export type SetOwnerOverviewLoadingAction = {
  type: HubOwnerStoreActionTypes.SET_OWNER_OVERVIEW_LOADING;
  payload: boolean;
};

export type SetCareteamRoleLoadingAction = {
  type: HubOwnerStoreActionTypes.SET_CARETEAM_ROLE_LOADING;
  payload: boolean;
};

export type SetDeleteContactLoadingAction = {
  type: HubOwnerStoreActionTypes.SET_DELETE_CONTACT_LOADING;
  payload: boolean;
};

export type SetOwnerContactsLoadingAction = {
  type: HubOwnerStoreActionTypes.SET_OWNER_CONTACTS_LOADING;
  payload: boolean;
};

// Response of GET /owners/:owner_id/survey_insights. Mirrors the generated
// client's return type so components can import it from the store.
export type SurveyInsightsPeriod = {
  days: number;
  from: string;
  to: string;
  granularity: 'day' | 'week';
  // IANA zone the window was cut on: the `time_zone` the request sent (the
  // browser's), else the server default Etc/UTC.
  time_zone: string;
};

export type SurveyInsightsDistributionEntry = {
  score: number;
  label: string | null;
  count: number;
  percentage: number;
};

export type SurveyInsightsTrendBucket = {
  bucket_start: string;
  bucket_end: string;
  // Days in the bucket with an answer; only the latest answer per day counts.
  response_count: number;
  // null when nothing was answered in the bucket (drawn as a gap).
  average_score: number | null;
};

export type SurveyInsightsBlock = {
  survey_type_id: number;
  survey_type: 'HUB_OVERALL_EXPERIENCE' | 'HUB_DAILY_WELLNESS_CHECK' | null;
  title: string;
  min_score: number;
  max_score: number;
  // Days in the window with an answer; only the latest answer per day counts.
  response_count: number;
  average_score: number | null;
  distribution: SurveyInsightsDistributionEntry[];
  trend: SurveyInsightsTrendBucket[];
};

export type OwnerSurveyInsights = {
  owner_id: number;
  period: SurveyInsightsPeriod;
  surveys: SurveyInsightsBlock[];
};

export type SurveyInsightsGranularity = SurveyInsightsPeriod['granularity'];

// The window the insights were fetched for (`requestedDays` is the API's
// `days` argument, not the label on the tab); the tab compares it with its
// current selection to decide whether a refetch is needed.
export type OwnerSurveyInsightsState = OwnerSurveyInsights & {
  ownerId: number;
  requestedDays: number;
};

export type RequestOwnerSurveyInsightsAction = {
  type: HubOwnerStoreActionTypes.REQUEST_OWNER_SURVEY_INSIGHTS;
  payload: {
    ownerId: number;
    requestedDays: number;
    granularity: SurveyInsightsGranularity;
    requestId: number;
  };
};

export type FetchOwnerSurveyInsightsSuccessAction = {
  type: HubOwnerStoreActionTypes.FETCH_OWNER_SURVEY_INSIGHTS_SUCCESS;
  payload: OwnerSurveyInsightsState & { requestId: number };
};

export type FetchOwnerSurveyInsightsFailureAction = {
  type: HubOwnerStoreActionTypes.FETCH_OWNER_SURVEY_INSIGHTS_FAILURE;
  payload: { requestId: number; error: string };
};

export type HubOwnerStoreAction =
  | CreateMedicationRollSuccessAction
  | GetMedicationScheduleTemplateAction
  | ResetImportMedRollFormAction
  | GetMedicationScheduleAction
  | SetLoadingAction
  | UnsetLoadingAction
  | GetMedicationEventHistoryAction
  | RequestMedicationEventImagesAction
  | GetMedicationEventImagesAction
  | FetchHubOwnersSuccessAction
  | AddHubOwnerSuccessAction
  | ResetAddHubOwnerSuccessAction
  | EditOwnerSuccessAction
  | SetActionStatusAction
  | ClearActionStatusAction
  | SetAddingHubOwnerAction
  | FetchOwnerContactsSuccessAction
  | FetchMedicationListSuccessAction
  | FetchOwnerOverviewSuccessAction
  | SetOwnerOverviewLoadingAction
  | SetCareteamRoleLoadingAction
  | SetDeleteContactLoadingAction
  | SetOwnerContactsLoadingAction
  | RequestOwnerSurveyInsightsAction
  | FetchOwnerSurveyInsightsSuccessAction
  | FetchOwnerSurveyInsightsFailureAction;

export interface CreateMedicationRollPost {
  customerId: number;
  batchId: number;
  pharmacyId: number;
  startDate: DateTime;
  scheduleTemplate: MedicationEvent[];
  scheduleTemplateDuration: number;
}

export interface MedicationEvent {
  prescribedTimeOfDay24: DateTime;
  desiredTimeOfDay24: DateTime;
  eventType?: MedEventType;
  pouches: Pouch[];
}

export interface Pouch {
  pouchNumber: number;
  drugs: Drug[];
}

export interface Drug {
  nationalDrugCode: string;
  rollPackaged: boolean;
  isPrn: boolean;
}

export interface MedicationSchedule {
  schedule_local_timezone: string;
  schedule_received_at: DateTime;
  upcoming_med_event_count: number;
  past_med_event_count: number;
  total_med_event_count: number;
  passed_due_dose_count: number;
  passed_due_pouch_count: number;
  med_rolls: MedicationRoll[];
  schedule: ScheduleMedicationEvent[];
}

export interface MedicationRoll {
  batch_id: number;
  starting_pouch_number: number;
  end_pouch_number: number;
  number_of_pouches: number;
  start_datetime: DateTime;
  end_datetime: DateTime;
}

export interface ScheduleMedicationEvent {
  prescribed_datetime: DateTime;
  desired_datetime: DateTime;
  prescribed_time_of_day_24: string;
  desired_time_of_day_24: string;
  dose_count: number;
  pouch_count: number;
  event_type: string;
  pouches: SchedulePouch[];
}

export interface SchedulePouch {
  pouch_number: number;
  batch_id: number;
  pouch_id: string;
  dose_count: number;
  status_counts: Map<string, number>;
  doses: ScheduleDose[];
}

export interface ScheduleDose {
  dose_id: number;
  national_drug_code: string;
  modified_at: DateTime;
  drug_name: string;
  tablet_count: string;
  dispensed_at: DateTime;
  medication_dose_status_id: number;
  administered_at: DateTime;
  administered_by: string;
  administered_timezone: string;
  received_at: DateTime;
  status: string;
}

export type MedEventHistoryState = {
  ownerId: number;
  medEventId: string;
  history: MedEventHistory;
};

/** Dispense images for one med event (OI-1368); keyed so a stale event never renders. */
export type MedEventImagesState = {
  ownerId: number;
  medEventId: string;
  images: MedEventImages;
};

interface Issue {
  title: string;
  category: string;
  severity: IssueSeverity.HIGH | IssueSeverity.LOW;
  device_id: number | null;
  created_at: string;
  description: string;
}

export type HubOwner = {
  customer_id: number;
  first_name: string;
  last_name: string;
  gender: string;
  gender_id: number;
  age: number;
  phone: string;
  email: string;
  date_of_birth: string;
  relationship_to_owner: string;
  device_id: number;
  facility_id: number;
  facility_name: string;
  sole_relationship: boolean;
  med_status: string;
  next_med_event: string;
  active_issue: Issue[];
  last_seen_online: string;
  serial_number: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  // Latest hub-experience label; GET /owners sends the label for every caller (OI-1769).
  last_survey_response?: string | null;
  // Latest daily wellness-check score (1-5). Needs OmcareRestServer to expose it on GET /owners.
  last_wellness_score?: number | null;
  facility_address?: string;
  location_name?: string;
  med_status_level?: string;
  parent_facility_name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  model_number?: string;
  hardware_version?: string;
  software_version?: string;
  device_synced_at?: string;
  wing?: string | null;
  room_number?: string | null;
};

export interface AddHubOwnerParams {
  first_name: string;
  last_name: string;
  // email/phone/gender are optional server-side; an email-less patient
  // simply can't log in, and omitting phone may trigger the duplicate
  // check's get_phone follow-up
  phone?: string;
  gender_id?: number;
  date_of_birth: string;
  email?: string;
  med_window_time_sec?: number;
  device_id?: number;
  address1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  facility_id?: number;
  /** Facility patients only. */
  wing?: string;
  room_number?: string;
}

export interface EditOwnerReqParams {
  first_name?: string;
  last_name?: string;
  /** null clears the number; the API rejects an empty string. */
  phone?: string | null;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  gender_id?: number;
  date_of_birth?: string;
  med_window_time_sec?: number;
  /** Facility patients only. */
  wing?: string | null;
  room_number?: string | null;
}

interface TableStatusInfo {
  icon: IconDefinition;
  color: string;
  text: string;
}

export interface CustomerCareHubRow {
  id: number;
  patient: string;
  facility_name: string;
  status: TableStatusInfo;
  last_seen_online: string;
  hub_serial_number: string;
  action: IconDefinition;
  facility_location: string;
  isOnline: boolean;
}
export interface CustomerCarePatientRow {
  id: number;
  patient_name: string;
  med_status_level: string;
  next_med_event: string | null;
  facility_location: string; // in API this is facility_name
  parent_facility_name: string;
}

export interface DashboardHubsDetailRow {
  id: number;
  patient: string;
  facility_name: string;
  status: TableStatusInfo;
  last_seen_online: string;
  hub_serial_number: string;
  action: IconDefinition;
}

// Server-directed follow-up on a 409 from addNewOwner (duplicate check):
// 'get_phone' — re-collect with a required phone so the server can verify,
// 'contact_cc' — confirmed duplicate, user must contact customer care.
export type DuplicateOwnerAction = 'get_phone' | 'contact_cc';

export interface HubActionStatus {
  success: boolean;
  error: string | null;
  data?: any;
  apiAction: HubApiActions | null;
  errorAction?: DuplicateOwnerAction | null;
}

export interface OwnerContact {
  customer_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  invite_status_id?: number;
  status?: string;
  careteam_role_id?: number;
  careteam_role?: string;
  facility_role_id?: number;
}

export type MedEventHistory = Array<{
  /**
   * The prescribed datetime of the medication event
   */
  med_event_id: string;
  /**
   * Status ID of the medication dose
   */
  status_id: number;
  /**
   * The timestamp when the medication dose status change occurred
   */
  occured_at: string;
  /**
   * The timezone of the owners hub
   */
  device_time_zone: string;
  event_data?: {
    dispensed_flow?: string;
    'skipped_reasons?'?: Array<string>;
  };
  sent_by: {
    /**
     * The ID of the customer
     */
    customer_id: number;
    /**
     * The first name of the customer
     */
    customer_first_name: string;
    /**
     * The last name of the customer
     */
    customer_last_name: string;
  };
  /**
   * Array of medication doses associated with the same occurrence time
   */
  doses: Array<{
    /**
     * The ID of the medication dose
     */
    id: number;
    /**
     * The national drug code for the medication
     */
    national_drug_code: string;
    /**
     * The name of the medication
     */
    drug_name: string;
  }>;
}>;
